import React, {useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { Typography, Button, Autocomplete, TextField, CircularProgress } from "@mui/material";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import {getBatchDefects, getBatchDefectsPage, getBatchDefectsByBatchCode, searchBatchByCode} from "../../utilityFunctions/WarehouseUtil.js";
import { PuffLoader } from "react-spinners";
import { css } from "@emotion/react";
import Colors from "../../components/ui/Colors";
import swal from 'sweetalert';
import Pagination from '@mui/material/Pagination';

const cssLoader = css`
    display: block;
    margin-left: auto;
    margin-right: auto;
`;

const dateOptions = {
    // weekday: "short",
    day: "numeric",
    month: "numeric",
    year: "numeric",
};

const pageSize = 40;

export default function BatchDefectsReport(props) {

    const{loginUser} = props;

    const[batchDefects, setBatchDefects] = useState([]);
    const[loading, setLoading] = useState(false);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    // const [pageSize] = useState(40);
    
    // Batch code filter state
    const [batchCodeInput, setBatchCodeInput] = useState('');
    const [batchCodeOptions, setBatchCodeOptions] = useState([]);
    const [selectedBatchCode, setSelectedBatchCode] = useState(null);
    const [searchingBatchCodes, setSearchingBatchCodes] = useState(false);

    useEffect(() => {
        const initialData = async () => {
            await loadInitialData();
        }
        initialData();
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        
        const pageRequest = {
            page: currentPage,
            size: pageSize
        };
        
        const data = await getBatchDefectsPage(pageRequest, loginUser.jwtToken);
        setLoading(false);

        if(!data){
            swal({
                title: "Error",
                text: "No se pudieron obtener los defectos de los lotes",
                icon: "error",
            });
            return;
        }

        setBatchDefects(data.content);
        setTotalPages(data.totalPages);
    }
    
    // Function to handle page change
    const handlePageChange = async (event, value) => {
        setCurrentPage(value - 1); // MUI Pagination is 1-indexed, but our API is 0-indexed
        
        setLoading(true);
        
        let defectsData;
        
        if (selectedBatchCode) {
            // If a batch code is selected, use the batch code filter
            const batchCodeRequest = {
                batchCode: selectedBatchCode,
                page: value - 1,
                size: pageSize
            };
            defectsData = await getBatchDefectsByBatchCode(batchCodeRequest, loginUser.jwtToken);
        } else {
            // Otherwise, just get the page
            const pageRequest = {
                page: value - 1,
                size: pageSize
            };
            defectsData = await getBatchDefectsPage(pageRequest, loginUser.jwtToken);
        }
        
        if (defectsData) {
            setBatchDefects(defectsData.content);
            setTotalPages(defectsData.totalPages);
        }

        setLoading(false);
    };
    
    // Function to search for batch codes
    const handleBatchCodeSearch = async (inputValue) => {
        setBatchCodeInput(inputValue);
        
        // Only search if input is at least 3 characters
        if (inputValue && inputValue.length >= 3) {
            setSearchingBatchCodes(true);
            const searchRequest = {
                batchCode: inputValue
            };
            
            const result = await searchBatchByCode(searchRequest, loginUser.jwtToken);
            if (result && result.batchCodes) {
                setBatchCodeOptions(result.batchCodes);
            }
            setSearchingBatchCodes(false);
        } else {
            setBatchCodeOptions([]);
        }
    };
    
    // Function to filter defects by batch code
    const filterByBatchCode = async (batchCode) => {
        setSelectedBatchCode(batchCode);
        
        if (!batchCode) {
            // If batch code is cleared, reset to regular view
            await loadInitialData();
            return;
        }
        
        setLoading(true);
        setCurrentPage(0);
        
        const batchCodeRequest = {
            batchCode: batchCode,
            page: 0,
            size: pageSize
        };
        
        const defectsData = await getBatchDefectsByBatchCode(batchCodeRequest, loginUser.jwtToken);
        
        if (defectsData && defectsData.content) {
            setBatchDefects(defectsData.content);
            setTotalPages(defectsData.totalPages);
            
            if (defectsData.content.length === 0) {
                swal({
                    title: "No se encontraron resultados",
                    text: `No se encontraron defectos con lote: ${batchCode}`,
                    icon: "info",
                    buttons: "OK"
                });
            } 
        } else {
            // If there was an error or no results, show a message
            swal({
                title: "Error en la búsqueda",
                text: "Ocurrió un error al buscar el lote especificado.",
                icon: "error",
                buttons: "OK"
            });
        }
        
        setLoading(false);
    };
    
    // Function to clear filters and reset to initial view
    const clearFilters = async () => {
        setSelectedBatchCode(null);
        setBatchCodeInput('');
        setCurrentPage(0);
        await loadInitialData();
    };
    
    return (
        <Box sx={{display: "flex", justifyContent: "center", width: "100%", height: "100%"}}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: {xs: "start", sm: "start"},
                    width: "90%",
                    height: "100%",
                    textAlign: "center",
                    overflowY: "scroll",
                }}
            >
                {loading ? <PuffLoader size={100} color={Colors.secondary} css={cssLoader} /> : 
                <>
                <Grid container>
                    {/* Batch Code Autocomplete - Centered above the list */}
                    <Grid item xs={12} sm={12} md={12} sx={{display: "flex", justifyContent: "center", mt: 2, mb: 2}}>
                        <Box sx={{width: "40%"}}>
                            <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textAlign: "center", mb: 1}}>
                                Buscar por Lote
                            </Typography>
                            <Autocomplete
                                value={selectedBatchCode}
                                onChange={(event, newValue) => {
                                    filterByBatchCode(newValue);
                                }}
                                inputValue={batchCodeInput}
                                onInputChange={(event, newInputValue) => {
                                    handleBatchCodeSearch(newInputValue);
                                }}
                                options={batchCodeOptions}
                                loading={searchingBatchCodes}
                                loadingText="Buscando lotes..."
                                noOptionsText={batchCodeInput.length >= 3 ? "No se encontraron lotes" : "Ingrese al menos 3 caracteres"}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Ingrese código de lote (mínimo 3 caracteres)"
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {searchingBatchCodes ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            {selectedBatchCode && (
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    size="small"
                                    onClick={clearFilters}
                                    sx={{ mt: 1, backgroundColor: Colors.primary }}
                                >
                                    Limpiar Filtro
                                </Button>
                            )}
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={12} md={12}>
                        <Grid container sx={{marginTop: "1rem", width: "100%", justifyContent: "center"}}>
                            <Grid item xs={2} sm={2} md={2} sx={{display: "flex", justifyContent: "center", textAlign: "center"}}>
                                <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: 'underline',}}>Código Averías</Typography>
                            </Grid>
                            <Grid item xs={2} sm={2} md={2} sx={{display: "flex", justifyContent: "center", textAlign: "center"}}>
                                <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: 'underline',}}>Código Estiba</Typography>
                            </Grid>
                            <Grid item xs={2} sm={2} md={2} sx={{display: "flex", justifyContent: "center", textAlign: "center"}}>
                                <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: 'underline',}}>Fecha</Typography>
                            </Grid>
                            <Grid item xs={2} sm={2} md={2} sx={{display: "flex", justifyContent: "center", textAlign: "center"}}>
                                <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: 'underline',}}>Cantidad</Typography>
                            </Grid>
                            <Grid item xs={2} sm={2} md={2} sx={{display: "flex", justifyContent: "center", textAlign: "center"}}>
                                <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: 'underline',}}>Lote</Typography>
                            </Grid>
                            <Grid item xs={2} sm={2} md={2} sx={{display: "flex", justifyContent: "center", textAlign: "center"}}>
                                <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: 'underline',}}>Usuario</Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    {batchDefects && batchDefects.length > 0 &&
                        <Grid item xs={12} sm={12} md={12}>
                            <List disablePadding>
                                {batchDefects.map((batchDefect) => (
                                    <ListItem key={batchDefect.id} divider disableGutters sx={{marginTop: "0.8rem", marginBottom: "0.8rem"}}>
                                        <Grid container sx={{width: "100%", justifyContent: "center"}}>
                                            <Grid item xs={2} sm={2} md={2} sx={{display: "flex", justifyContent: "center", textAlign: "center"}}>
                                                <Typography variant="body2" sx={{color: "black"}}>{batchDefect.id}</Typography>
                                            </Grid>
                                            <Grid item xs={2} sm={2} md={2} sx={{display: "flex", justifyContent: "center", textAlign: "center"}}>
                                                <Typography variant="body2" sx={{color: "black"}}>{batchDefect.batchId}</Typography>
                                            </Grid>
                                            <Grid item xs={2} sm={2} md={2} sx={{display: "flex", justifyContent: "center", textAlign: "center"}}>
                                                <Typography variant="body2" sx={{color: "black"}}>{new Intl.DateTimeFormat('es-CO', dateOptions).format(new Date(batchDefect.timestamp))}</Typography>
                                            </Grid>
                                            <Grid item xs={2} sm={2} md={2} sx={{display: "flex", justifyContent: "center", textAlign: "center"}}>
                                                <Typography variant="body2" sx={{color: "black"}}>{batchDefect.amount}</Typography>
                                            </Grid>
                                            <Grid item xs={2} sm={2} md={2} sx={{display: "flex", justifyContent: "center", textAlign: "center"}}>
                                                <Typography variant="body2" sx={{color: "black"}}>{batchDefect.batchCode}</Typography>
                                            </Grid>
                                            <Grid item xs={2} sm={2} md={2} sx={{display: "flex", justifyContent: "center", textAlign: "center"}}>
                                                <Typography variant="body2" sx={{color: "black"}}>{batchDefect.username ? batchDefect.username : ""}</Typography>
                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    }
                    
                    {/* Pagination component */}
                    <Grid item xs={12} sm={12} md={12} sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
                        <Pagination 
                            count={totalPages} 
                            page={currentPage + 1} 
                            onChange={handlePageChange} 
                            color="primary" 
                        />
                    </Grid>
                </Grid>
                </>
            }
            </Box>
        </Box>
    )
}
